import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowRight,
  BriefcaseBusiness,
  ClipboardCheck,
  Hammer,
  Layers3,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const subtlePlaceholderClass = "placeholder:text-muted-foreground/40";

const initialFormState = {
  fullName: "",
  email: "",
  phone: "",
  location: "",
  experience: "",
  message: "",
};

const careerBenefits = [
  {
    icon: Layers3,
    title: "End-to-End Exposure",
    description:
      "Work across design coordination, site execution, procurement, and client delivery in one integrated studio.",
  },
  {
    icon: Hammer,
    title: "Build Real Projects",
    description:
      "Contribute to commercial, residential, hospitality, and turnkey interior projects that move from concept to handover.",
  },
  {
    icon: Users,
    title: "Direct Team Access",
    description:
      "Collaborate closely with leadership and experienced project teams instead of working through rigid layers.",
  },
  {
    icon: Sparkles,
    title: "Growth With Ownership",
    description:
      "Take responsibility early, sharpen decision-making, and grow with a company that values initiative and execution.",
  },
];

const openPositions = [
  {
    title: "Interior Site Engineer",
    type: "Full Time",
    locations: "Bengaluru",
    description:
      "Lead day-to-day site coordination for interior fit-out projects, ensuring execution quality, safety, and timely progress.",
    requirements: [
      "Diploma or degree in civil engineering, interior execution, or a related field",
      "2-5 years of site execution experience in interiors or construction",
      "Ability to read drawings, coordinate vendors, and manage site progress",
    ],
    benefits: ["Project ownership", "Site travel allowance", "Growth in turnkey execution"],
  },
  {
    title: "Project Coordinator",
    type: "Full Time",
    locations: "Bengaluru",
    description:
      "Support scheduling, documentation, vendor follow-ups, and communication across clients, designers, and execution teams.",
    requirements: [
      "1-4 years of coordination experience in construction, interiors, or operations",
      "Strong communication and documentation skills",
      "Comfort working with timelines, BOQs, and project trackers",
    ],
    benefits: ["Cross-functional exposure", "Client-facing experience", "Structured growth path"],
  },
  {
    title: "Procurement & Vendor Executive",
    type: "Full Time",
    locations: "Bengaluru",
    description:
      "Manage sourcing, vendor comparisons, purchase follow-ups, and material readiness for ongoing fit-out projects.",
    requirements: [
      "Experience in procurement, purchase, or vendor coordination",
      "Good commercial understanding of materials and pricing",
      "Ability to balance quality, delivery timelines, and budget expectations",
    ],
    benefits: ["Vendor network development", "Commercial exposure", "Operational ownership"],
  },
  {
    title: "Operations & Admin Executive",
    type: "Full Time",
    locations: "Bengaluru",
    description:
      "Handle office coordination, documentation, support logistics, and internal administrative processes that keep projects moving smoothly.",
    requirements: [
      "1-3 years of admin or operations experience",
      "Strong organization and follow-up skills",
      "Comfort with office systems, documents, and team coordination",
    ],
    benefits: ["Stable team environment", "Broader business exposure", "Multi-team collaboration"],
  },
];

const CareersPage = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [pendingRole, setPendingRole] = useState(null);
  const [showRoleSwitchConfirm, setShowRoleSwitchConfirm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [resumeFile, setResumeFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const captchaRef = useRef(null);
  const closeTimerRef = useRef(null);

  const isOperationsRole = selectedRole?.title === "Operations & Admin Executive";
  const isDirty = useMemo(
    () =>
      Object.values(formData).some(Boolean) ||
      Boolean(resumeFile) ||
      Boolean(captchaToken),
    [formData, resumeFile, captchaToken]
  );

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setFormData(initialFormState);
    setResumeFile(null);
    setCaptchaToken("");
    setIsSubmitting(false);
    captchaRef.current?.reset();
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    captchaRef.current?.reset();
    toast.error("reCAPTCHA expired. Please verify again.");
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken("");
    captchaRef.current?.reset();
    toast.error("reCAPTCHA could not be verified. Please try again.");
  };

  const updatePhone = (value) => {
    const normalizedValue = value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: normalizedValue }));
  };

  const openRoleApplication = (role) => {
    if (selectedRole && isDirty) {
      setPendingRole(role);
      setShowRoleSwitchConfirm(true);
      return;
    }

    resetForm();
    setSelectedRole(role);
  };

  const confirmRoleSwitch = () => {
    resetForm();
    setSelectedRole(pendingRole);
    setPendingRole(null);
    setShowRoleSwitchConfirm(false);
  };

  const closeApplication = (open) => {
    if (!open) {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
      setIsSubmitted(false);
      setSelectedRole(null);
      setPendingRole(null);
      setShowRoleSwitchConfirm(false);
      resetForm();
    }
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.email || !formData.phone || !formData.location || !formData.experience) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    if (!PHONE_REGEX.test(formData.phone)) {
      toast.error("Phone number must be a valid 10-digit Indian mobile number.");
      return false;
    }

    if (isOperationsRole && !resumeFile) {
      toast.error("Resume upload is required for Operations & Admin roles.");
      return false;
    }

    if (!RECAPTCHA_SITE_KEY) {
      toast.error("reCAPTCHA site key is missing. Add REACT_APP_RECAPTCHA_SITE_KEY to enable applications.");
      return false;
    }

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedRole || !validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("full_name", formData.fullName);
      payload.append("email", formData.email);
      payload.append("phone", formData.phone);
      payload.append("location", formData.location);
      payload.append("experience", formData.experience);
      payload.append("position", selectedRole.title);
      payload.append("message", formData.message);
      payload.append("recaptcha_token", captchaToken);

      if (resumeFile) {
        payload.append("resume", resumeFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/careers`, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error("Unable to submit application");
      }

      setIsSubmitted(true);
      toast.success("Application submitted successfully.");
      resetForm();

      closeTimerRef.current = window.setTimeout(() => {
        setSelectedRole(null);
        setIsSubmitted(false);
      }, 4000);
    } catch (error) {
      toast.error("Could not submit your application right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden bg-background">
      <section className="relative min-h-[55vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&auto=format&fit=crop"
            alt="BRYN careers"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 via-charcoal/80 to-charcoal/55" />
        </div>

        <div className="relative z-10 container mx-auto px-4 pb-16 pt-28 lg:px-8 lg:pb-24">
          <div className="max-w-3xl">
            <Badge className="mb-5 border-0 bg-primary/20 text-primary-foreground">
              CAREERS
            </Badge>
            <h1 className="font-display text-4xl font-bold leading-tight text-primary-foreground sm:text-5xl lg:text-6xl">
              JOIN OUR TEAM
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              Join a team that designs, builds, and delivers spaces with care.
              At BRYN, we value execution, ownership, and people who want to shape
              meaningful interior and construction projects from the inside out.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
              Why Join Us
            </Badge>
            <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
              Build your career where design meets execution
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {careerBenefits.map((benefit) => (
              <Card
                key={benefit.title}
                className="border-0 bg-card shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-elegant"
              >
                <CardContent className="p-6">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <benefit.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-12 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
                Open Positions
              </Badge>
              <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
                Opportunities across interiors, coordination, and execution
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              We are hiring people who are detail-oriented, proactive, and excited
              by real-world project delivery.
            </p>
          </div>

          <div className="space-y-6">
            {openPositions.map((role) => (
              <Card key={role.title} className="border-0 bg-card shadow-md">
                <CardContent className="p-6 lg:p-8">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-3xl">
                      <div className="mb-4 flex flex-wrap gap-3">
                        <Badge className="border-0 bg-primary/10 text-primary hover:bg-primary/10">
                          {role.type}
                        </Badge>
                        <Badge variant="outline" className="border-border text-muted-foreground">
                          {role.locations}
                        </Badge>
                      </div>
                      <h3 className="font-display text-2xl font-semibold text-foreground">
                        {role.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {role.description}
                      </p>

                      <div className="mt-6 grid gap-6 lg:grid-cols-2">
                        <div>
                          <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-foreground/70">
                            Requirements
                          </h4>
                          <ul className="space-y-3">
                            {role.requirements.map((item) => (
                              <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-foreground/70">
                            What You Get
                          </h4>
                          <ul className="space-y-3">
                            {role.benefits.map((item) => (
                              <li key={item} className="flex gap-3 text-sm text-muted-foreground">
                                <BriefcaseBusiness className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => openRoleApplication(role)}
                      className="bg-primary px-6 hover:bg-primary/90"
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="overflow-hidden border-0 bg-charcoal text-primary-foreground shadow-elegant">
            <CardContent className="grid gap-8 p-8 lg:grid-cols-[1.1fr_0.9fr] lg:p-12">
              <div>
                <Badge variant="outline" className="mb-4 border-primary/40 text-primary">
                  How To Apply
                </Badge>
                <h2 className="font-display text-3xl font-bold sm:text-4xl">
                  Share your experience and let&apos;s start the conversation.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/75">
                  Apply for an open role above, or reach out directly if you believe
                  your experience aligns with BRYN&apos;s design, procurement, or
                  execution-focused work.
                </p>
              </div>

              <div className="rounded-[28px] bg-white/5 p-6 sm:p-8">
                <div className="space-y-4">
                  <a href="tel:9986435437" className="flex items-start gap-3 text-primary-foreground/85">
                    <Phone className="mt-0.5 h-5 w-5 text-primary" />
                    <span>9986435437</span>
                  </a>
                  <a href="mailto:care@bryninteriors.com" className="flex items-start gap-3 text-primary-foreground/85">
                    <Mail className="mt-0.5 h-5 w-5 text-primary" />
                    <span>care@bryninteriors.com</span>
                  </a>
                  <div className="flex items-start gap-3 text-primary-foreground/85">
                    <MapPin className="mt-0.5 h-5 w-5 text-primary" />
                    <span>#106, House of Lords, St Mark&apos;s Road, Bengaluru - 560001</span>
                  </div>
                </div>

                <Button asChild className="mt-8 w-full bg-primary hover:bg-primary/90">
                  <Link to="/contact#contact-form">
                    Contact Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Dialog open={Boolean(selectedRole)} onOpenChange={closeApplication}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
          <div className="border-b border-border/60 px-6 py-5 lg:px-8">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-foreground">
                {isSubmitted ? "Application Submitted" : "Apply for the Role"}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-muted-foreground">
                {isSubmitted
                  ? "Thank you for applying. We will review your profile and connect with you soon."
                  : `You are applying for ${selectedRole?.title}. Fill in your details below.`}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-6 lg:px-8 lg:py-8">
            {isSubmitted ? (
              <div className="rounded-3xl bg-success/5 p-8 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                  <ClipboardCheck className="h-8 w-8 text-success" />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-foreground">
                  Application sent successfully
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Your form has been submitted and this modal will close automatically.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="career-full-name">Full Name *</Label>
                    <Input
                      id="career-full-name"
                      value={formData.fullName}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, fullName: event.target.value }))
                      }
                      placeholder="Full Name"
                      className={`h-12 ${subtlePlaceholderClass}`}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="career-email">Email *</Label>
                    <Input
                      id="career-email"
                      type="email"
                      value={formData.email}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, email: event.target.value }))
                      }
                      placeholder="Email Address"
                      className={`h-12 ${subtlePlaceholderClass}`}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="career-phone">Phone *</Label>
                    <div className="flex h-12 overflow-hidden rounded-md border border-input bg-background">
                      <div className="flex items-center gap-2 border-r border-input bg-muted/60 px-3 text-sm font-medium text-foreground">
                        <span className="text-base" aria-hidden="true">🇮🇳</span>
                        +91
                      </div>
                      <Input
                        id="career-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(event) => updatePhone(event.target.value)}
                        placeholder="Phone Number"
                        inputMode="numeric"
                        maxLength={10}
                        className={`h-full flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 ${subtlePlaceholderClass}`}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="career-location">Location *</Label>
                    <Input
                      id="career-location"
                      value={formData.location}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, location: event.target.value }))
                      }
                      placeholder="Current Location"
                      className={`h-12 ${subtlePlaceholderClass}`}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="career-experience">Experience (Years) *</Label>
                    <Input
                      id="career-experience"
                      value={formData.experience}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, experience: event.target.value }))
                      }
                      placeholder="Years of Experience"
                      className={`h-12 ${subtlePlaceholderClass}`}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="career-position">Position</Label>
                    <Input
                      id="career-position"
                      value={selectedRole?.title || ""}
                      className="h-12 bg-muted"
                      readOnly
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="career-message">Message</Label>
                    <Textarea
                      id="career-message"
                      value={formData.message}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, message: event.target.value }))
                      }
                      placeholder="Tell us briefly about your project background or relevant experience."
                      className={`min-h-[130px] ${subtlePlaceholderClass}`}
                    />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="career-resume">
                    Resume {isOperationsRole ? "*" : "(Optional)"}
                  </Label>
                  <Input
                    id="career-resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(event) => setResumeFile(event.target.files?.[0] || null)}
                    className="h-12 file:mr-4 file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary"
                  />
                </div>

                <div className="space-y-2">
                  <Label>reCAPTCHA *</Label>
                  {RECAPTCHA_SITE_KEY ? (
                    <div className="overflow-x-auto rounded-xl border border-border/60 p-3">
                      <ReCAPTCHA
                        ref={captchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(value) => setCaptchaToken(value || "")}
                        onExpired={handleCaptchaExpired}
                        onErrored={handleCaptchaErrored}
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground">
                      Add `REACT_APP_RECAPTCHA_SITE_KEY` to enable reCAPTCHA on this form.
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </DialogFooter>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showRoleSwitchConfirm} onOpenChange={setShowRoleSwitchConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch role application?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved application details. Switching roles will clear the
              current form and open the new position.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingRole(null)}>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRoleSwitch}>Switch Role</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CareersPage;
